import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { IconPalette, IconMoon, IconSunrise, IconSun, IconSunset } from "@tabler/icons-react";
import "./PaletteSelector.css";

const OPTIONS = [
  { value: "morning", label: "Morning", Icon: IconSunrise },
  { value: "afternoon", label: "Afternoon", Icon: IconSun },
  { value: "evening", label: "Evening", Icon: IconSunset },
  { value: "night", label: "Night", Icon: IconMoon },
];

// Home-only time-of-day palette switch, rendered into the ContactAudit dock
// (.dock-slot). Opens a vertical drop-up list above the dock. The list is
// portaled to <body>: inside the dock its glass blur would only see the dock
// (a backdrop-filter parent cuts nested ones off from the page).
export default function PaletteSelector({ value, onChange }) {
  const [slot, setSlot] = useState(null);
  const [open, setOpen] = useState(false); // false, or the menu's position
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => setSlot(document.querySelector(".dock-slot")), []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) =>
      !ref.current?.contains(e.target) && !menuRef.current?.contains(e.target) && close();
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  });

  // Rises out of the dock, options staggering up behind it
  useGSAP(
    () => {
      if (!open) return;
      gsap.fromTo(menuRef.current, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.35, ease: "power3.out" });
      gsap.fromTo(
        menuRef.current.children,
        { autoAlpha: 0, y: 8 },
        { autoAlpha: 1, y: 0, duration: 0.3, stagger: -0.04, delay: 0.05, ease: "power3.out" },
      );
    },
    { dependencies: [Boolean(open)] },
  );

  const close = () =>
    gsap.to(menuRef.current, {
      autoAlpha: 0,
      y: 12,
      duration: 0.2,
      ease: "power2.in",
      overwrite: true,
      onComplete: () => setOpen(false),
    });

  if (!slot) return null;

  const toggle = () => {
    if (open) return close();
    const dock = ref.current.closest(".contact-audit-container").getBoundingClientRect();
    setOpen({ left: dock.left, bottom: window.innerHeight - dock.top });
  };

  return createPortal(
    <div className="palette-selector" ref={ref}>
      {open && createPortal(
        <div
          className="palette-selector-menu"
          role="menu"
          aria-label="Color palette"
          ref={menuRef}
          style={{ left: open.left, bottom: open.bottom }}
        >
          {OPTIONS.map(({ value: v, label, Icon }) => (
            <button
              key={v}
              type="button"
              role="menuitemradio"
              aria-checked={v === value}
              className={`btn palette-selector-option ${v === value ? "is-active" : ""}`}
              onClick={() => {
                onChange(v);
                close();
              }}
            >
              <Icon size={20} stroke={1.5} />
              {label}
            </button>
          ))}
        </div>,
        document.body,
      )}
      <button
        type="button"
        className="btn btn--icon"
        aria-label="Change color palette"
        aria-expanded={Boolean(open)}
        title="Palette follows your time of day. Try another."
        onClick={toggle}
      >
        <IconPalette size={20} stroke={1.5} />
      </button>
    </div>,
    slot,
  );
}

PaletteSelector.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
